import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "status"]

  static values = {
    enderecoSelector: String,
    complementoSelector: String,
    bairroSelector: String,
    cidadeSelector: String,
    ufSelector: String,
    paisSelector: String,
    numeroSelector: String,

    cidadeIdSelector: String,
    estadoIdSelector: String,
    paisIdSelector: String,

    endpoint: { type: String, default: "https://viacep.com.br/ws" },

    cidadeLookupUrl: String,
    estadoLookupUrl: String,
    paisLookupUrl: String,

    overwriteManual: { type: Boolean, default: false },
    clearOnError: { type: Boolean, default: false },
    focusNumeroAfterFill: { type: Boolean, default: true }
  }

  connect() {
    this.buscando = false
    this.ultimoCepBuscado = null
    this.manuallyTouched = new Set()
    this.programmaticUpdate = false

    this.bindManualTracking()
  }

  bloquearLetras(event) {
    if (this.teclaPermitida(event)) return
    if (!/[0-9]/.test(event.key)) event.preventDefault()
  }

  colarSoDigitos(event) {
    event.preventDefault()

    const texto = (event.clipboardData || window.clipboardData).getData("text")
    const digitos = this.soDigitos(texto).slice(0, 8)

    this.inputTarget.value = this.aplicarMascara(digitos)
    this.buscarSeCompleto()
  }

  mascara() {
    const digitos = this.soDigitos(this.inputTarget.value).slice(0, 8)
    this.inputTarget.value = this.aplicarMascara(digitos)
    this.buscarSeCompleto()
  }

  async blur() {
    await this.buscarSeCompleto()
  }

  async buscarSeCompleto() {
    const cep = this.cepSemMascara()

    if (cep.length !== 8) {
      this.setStatus("")
      return
    }

    if (this.buscando) return
    if (this.ultimoCepBuscado === cep) return

    this.buscando = true
    this.marcarCarregando(true)
    this.setStatus("Buscando CEP...")

    try {
      const response = await fetch(`${this.endpointValue}/${cep}/json/`, {
        headers: { Accept: "application/json" }
      })

      if (!response.ok) {
        throw new Error(`Erro ao consultar CEP: ${response.status}`)
      }

      const data = await response.json()

      if (data.erro) {
        this.marcarInvalido()
        this.setStatus("CEP não encontrado.")

        if (this.clearOnErrorValue) {
          this.limparCamposEndereco()
        }
        return
      }

      const preenchido = await this.preencherCampos(data)
      if (!preenchido) {
        this.marcarInvalido()
        this.setStatus("CEP encontrado, mas não foi possível preencher cidade, estado e país.")
        return
      }

      this.desmarcarInvalido()
      this.setStatus("CEP encontrado.")
      this.ultimoCepBuscado = cep

      this.element.dispatchEvent(new CustomEvent("cep:preenchido", {
        bubbles: true,
        detail: { ...data }
      }))
    } catch (error) {
      console.error("[cep-field] erro ao buscar CEP:", error)
      this.marcarInvalido()
      this.setStatus("Erro ao consultar o CEP.")
    } finally {
      this.marcarCarregando(false)
      this.buscando = false
    }
  }

  async preencherCampos(data) {
    this.setFieldValueIfAllowed(this.enderecoSelectorValue, data.logradouro)
    this.setFieldValueIfAllowed(this.complementoSelectorValue, data.complemento)
    this.setFieldValueIfAllowed(this.bairroSelectorValue, data.bairro)
    this.setFieldValueIfAllowed(this.cidadeSelectorValue, data.localidade)
    this.setFieldValueIfAllowed(this.ufSelectorValue, data.uf)
    this.setFieldValueIfAllowed(this.paisSelectorValue, "Brasil")

    const relacionadosPreenchidos = await this.preencherIdsRelacionados(data)

    if (this.focusNumeroAfterFillValue && this.hasNumeroSelectorValue) {
      const numeroField = this.findField(this.numeroSelectorValue)
      if (numeroField && !numeroField.value) numeroField.focus()
    }

    return relacionadosPreenchidos
  }

  async preencherIdsRelacionados(data) {
    const jobs = []

    if (this.hasCidadeIdSelectorValue && this.hasCidadeLookupUrlValue) {
      jobs.push(this.lookupCidadeComCascata(data))
    }

    if (!this.hasCidadeIdSelectorValue && this.hasEstadoIdSelectorValue && this.hasEstadoLookupUrlValue) {
      jobs.push(
        this.lookupAndSet({
          url: this.estadoLookupUrlValue,
          selector: this.estadoIdSelectorValue,
          payload: { sigla: data.uf }
        })
      )
    }

    if (!this.hasCidadeIdSelectorValue && this.hasPaisIdSelectorValue && this.hasPaisLookupUrlValue) {
      jobs.push(
        this.lookupAndSet({
          url: this.paisLookupUrlValue,
          selector: this.paisIdSelectorValue,
          payload: { nome: "Brasil" }
        })
      )
    }

    const results = await Promise.all(jobs)
    return results.every((result) => result !== false)
  }

  async lookupCidadeComCascata(data) {
    const cidadeField = this.findField(this.cidadeIdSelectorValue)
    if (!cidadeField) return false
    if (!this.shouldOverwriteField(cidadeField)) return true

    try {
      const url = `${this.cidadeLookupUrlValue}?${new URLSearchParams({ nome: data.localidade, uf: data.uf }).toString()}`
      const response = await fetch(url, { headers: { Accept: "application/json" } })

      if (!response.ok) return false

      const result = await response.json()
      if (!result || !result.id) return false

      let preenchido = this.applyValue(cidadeField, result.id, result.nome)

      if (this.hasEstadoIdSelectorValue) {
        const estadoField = this.findField(this.estadoIdSelectorValue)
        if (!result.estado_id || !estadoField) {
          preenchido = false
        } else if (this.shouldOverwriteField(estadoField)) {
          preenchido = this.applyValue(estadoField, result.estado_id, result.estado_nome) && preenchido
        }
      }

      if (this.hasPaisIdSelectorValue) {
        const paisField = this.findField(this.paisIdSelectorValue)
        if (!result.pais_id || !paisField) {
          preenchido = false
        } else if (this.shouldOverwriteField(paisField)) {
          preenchido = this.applyValue(paisField, result.pais_id, result.pais_nome) && preenchido
        }
      }

      return preenchido
    } catch (error) {
      console.error("[cep-field] erro no lookup de cidade:", error)
      return false
    }
  }

  async lookupAndSet({ url, selector, payload }) {
    const field = this.findField(selector)
    if (!field) return false
    if (!this.shouldOverwriteField(field)) return true

    try {
      const response = await fetch(`${url}?${new URLSearchParams(payload).toString()}`, {
        headers: { Accept: "application/json" }
      })

      if (!response.ok) return false

      const data = await response.json()

      if (data && data.id) {
        return this.applyValue(field, data.id, data.nome)
      }

      return false
    } catch (error) {
      console.error("[cep-field] erro no lookup relacionado:", error)
      return false
    }
  }

  limparCamposEndereco() {
    this.clearFieldIfAllowed(this.enderecoSelectorValue)
    this.clearFieldIfAllowed(this.complementoSelectorValue)
    this.clearFieldIfAllowed(this.bairroSelectorValue)
    this.clearFieldIfAllowed(this.cidadeSelectorValue)
    this.clearFieldIfAllowed(this.ufSelectorValue)
    this.clearFieldIfAllowed(this.paisSelectorValue)
  }

  clearFieldIfAllowed(selector) {
    if (!selector) return
    const field = this.findField(selector)
    if (!field) return
    if (!this.shouldOverwriteField(field)) return

    this.applyValue(field, "")
  }

  setFieldValueIfAllowed(selector, value) {
    if (!selector) return

    const field = this.findField(selector)
    if (!field) return
    if (!this.shouldOverwriteField(field)) return

    if (field.tagName === "SELECT" && !this.isTomSelect(field)) {
      this.setSelectValue(field, value)
      return
    }

    this.applyValue(field, value)
  }

  setSelectValue(field, value) {
    if (!value) return

    const normalizedValue = this.normalize(value)

    const option = Array.from(field.options).find((opt) => {
      return this.normalize(opt.text) === normalizedValue ||
             this.normalize(opt.value) === normalizedValue
    })

    if (!option) return

    this.applyValue(field, option.value)
  }

  isTomSelect(field) {
    return field && field.tomselect && typeof field.tomselect.setValue === "function"
  }

  applyValue(field, value, text = null) {
    this.programmaticUpdate = true

    if (this.isTomSelect(field)) {
      const ts = field.tomselect
      const normalizedValue = String(value)

      const valueField = ts.settings.valueField || "value"
      const labelField = ts.settings.labelField || "text"
      const displayText = text || normalizedValue

      if (!ts.options[normalizedValue]) {
        ts.addOption({
          [valueField]: normalizedValue,
          [labelField]: displayText
        })
      }

      ts.setValue(normalizedValue, true)
      ts.refreshItems()

      field.value = normalizedValue
      field.dispatchEvent(new Event("change", { bubbles: true }))
    } else {
      field.value = value
      field.dispatchEvent(new Event("input", { bubbles: true }))
      field.dispatchEvent(new Event("change", { bubbles: true }))
    }

    this.programmaticUpdate = false
    return String(field.value) === String(value)
  }

  shouldOverwriteField(field) {
    if (this.overwriteManualValue) return true
    if (!field) return false

    const selectorKey = field.id || field.name
    if (!selectorKey) return field.value === ""

    if (this.manuallyTouched.has(selectorKey)) return false

    return !field.value || String(field.value).trim() === ""
  }

  bindManualTracking() {
    const selectors = [
      this.enderecoSelectorValue,
      this.complementoSelectorValue,
      this.bairroSelectorValue,
      this.cidadeSelectorValue,
      this.ufSelectorValue,
      this.paisSelectorValue,
      this.numeroSelectorValue,
      this.cidadeIdSelectorValue,
      this.estadoIdSelectorValue,
      this.paisIdSelectorValue
    ].filter(Boolean)

    selectors.forEach((selector) => {
      const field = this.findField(selector)
      if (!field) return

      field.addEventListener("input", () => this.markTouched(field))
      field.addEventListener("change", () => this.markTouched(field))
    })
  }

  markTouched(field) {
    if (this.programmaticUpdate) return
    if (!field) return

    const key = field.id || field.name
    if (key) this.manuallyTouched.add(key)
  }

  findField(selector) {
    if (!selector) return null

    const form = this.inputTarget.form || this.element.closest("form")
    if (form) {
      const insideForm = form.querySelector(selector)
      if (insideForm) return insideForm
    }

    return document.querySelector(selector)
  }

  marcarCarregando(status) {
    this.inputTarget.toggleAttribute("aria-busy", status)
    this.inputTarget.classList.toggle("is-loading", status)
  }

  marcarInvalido() {
    this.inputTarget.classList.add("border-danger")
    this.inputTarget.classList.add("is-invalid")
  }

  desmarcarInvalido() {
    this.inputTarget.classList.remove("border-danger")
    this.inputTarget.classList.remove("is-invalid")
  }

  setStatus(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message || ""
  }

  teclaPermitida(event) {
    const teclas = [
      "Backspace", "Delete", "Tab", "Escape", "Enter",
      "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
      "Home", "End"
    ]

    return teclas.includes(event.key) || event.ctrlKey || event.metaKey
  }

  cepSemMascara() {
    return this.soDigitos(this.inputTarget.value)
  }

  soDigitos(valor) {
    return (valor || "").replace(/\D/g, "")
  }

  aplicarMascara(digitos) {
    if (!digitos) return ""
    if (digitos.length <= 2) return digitos
    if (digitos.length <= 5) return `${digitos.slice(0, 2)}.${digitos.slice(2)}`
    return `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}-${digitos.slice(5, 8)}`
  }

  normalize(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase()
  }
}
